import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { host } from '../../myGlobals';
import { Product, InventoryTransaction, InventoryIn, InventoryOut, CurrentStock, BatchInventory, Notification, Dashboard, ReportFilter } from '../models/warehouse.model';

const API_URL = host + 'api/';
const httpOptions = {
  headers: new HttpHeaders({ 'Content-Type': 'application/json' })
};

@Injectable({
  providedIn: 'root'
})
export class WarehouseService {

  constructor(private http: HttpClient) { }

  // Product endpoints
  createProduct(product: Product): Observable<Product> {
    return this.http.post<Product>(API_URL + 'products', product, httpOptions);
  }

  updateProduct(id: number, product: Product): Observable<Product> {
    return this.http.put<Product>(API_URL + 'products/' + id, product, httpOptions);
  }

  deleteProduct(id: number): Observable<any> {
    return this.http.delete(API_URL + 'products/' + id, httpOptions);
  }

  getProduct(id: number): Observable<Product> {
    return this.http.get<Product>(API_URL + 'products/' + id);
  }

  getProductBySku(sku: string): Observable<Product> {
    return this.http.get<Product>(API_URL + 'products/sku/' + sku);
  }

  getAllProducts(page: number = 0, size: number = 10): Observable<any> {
    return this.http.get<any>(API_URL + 'products', {
      params: new HttpParams().set('page', page.toString()).set('size', size.toString())
    });
  }

  searchProducts(searchTerm: string): Observable<Product[]> {
    return this.http.get<Product[]>(API_URL + 'products/search', {
      params: new HttpParams().set('searchTerm', searchTerm)
    });
  }

  getProductsByCategory(category: string): Observable<Product[]> {
    return this.http.get<Product[]>(API_URL + 'products/category/' + category);
  }

  getImportedProducts(): Observable<Product[]> {
    return this.http.get<Product[]>(API_URL + 'products/imported');
  }

  // Inventory endpoints
  recordInventoryIn(inventoryIn: InventoryIn): Observable<InventoryTransaction> {
    return this.http.post<InventoryTransaction>(API_URL + 'inventory/in', inventoryIn, httpOptions);
  }

  recordInventoryOut(inventoryOut: InventoryOut): Observable<InventoryTransaction> {
    return this.http.post<InventoryTransaction>(API_URL + 'inventory/out', inventoryOut, httpOptions);
  }

  getTransactionHistory(productId?: number, startDate?: string, endDate?: string, transactionType?: string, page?: number, size?: number): Observable<any> {
    let params = new HttpParams();
    if (productId) params = params.set('productId', productId.toString());
    if (startDate) params = params.set('startDate', startDate);
    if (endDate) params = params.set('endDate', endDate);
    // Only add transactionType parameter if it's defined and not null/empty
    if (transactionType && transactionType !== 'null' && transactionType.trim() !== '') {
      params = params.set('transactionType', transactionType);
    }
    if (page !== undefined && size !== undefined) {
      params = params.set('page', page.toString()).set('size', size.toString());
    }
    return this.http.get<any>(API_URL + 'inventory/transactions', { params });
  }

  getTransaction(id: number): Observable<InventoryTransaction> {
    return this.http.get<InventoryTransaction>(API_URL + 'inventory/transactions/' + id);
  }

  getAllCurrentStock(page: number = 0, size: number = 10): Observable<any> {
    return this.http.get<any>(API_URL + 'inventory/current', {
      params: new HttpParams().set('page', page.toString()).set('size', size.toString())
    });
  }

  getCurrentStock(productId: number): Observable<CurrentStock> {
    return this.http.get<CurrentStock>(API_URL + 'inventory/current/' + productId);
  }

  getTotalInventoryValue(): Observable<number> {
    return this.http.get<number>(API_URL + 'inventory/value');
  }

  getLowStockProducts(page?: number, size?: number): Observable<any> {
    let params = new HttpParams();
    if (page !== undefined && size !== undefined) {
      params = params.set('page', page.toString()).set('size', size.toString());
    }
    return this.http.get<any>(API_URL + 'inventory/low-stock', { params });
  }

  getOutOfStockProducts(): Observable<CurrentStock[]> {
    return this.http.get<CurrentStock[]>(API_URL + 'inventory/out-of-stock');
  }

  getLowStockImportedProducts(): Observable<CurrentStock[]> {
    return this.http.get<CurrentStock[]>(API_URL + 'inventory/low-stock/imported');
  }

  getExpiringBatches(daysBefore: number = 30, page?: number, size?: number): Observable<any> {
    let params = new HttpParams().set('daysBefore', daysBefore.toString());
    if (page !== undefined && size !== undefined) {
      params = params.set('page', page.toString()).set('size', size.toString());
    }
    return this.http.get<any>(API_URL + 'inventory/expiring', { params });
  }

  getBatchesByProduct(productId: number | null, page?: number, size?: number): Observable<any> {
    if (productId) {
      let params = new HttpParams();
      params = params.set('productId', productId.toString());
      if (page !== undefined && size !== undefined) {
        params = params.set('page', page.toString()).set('size', size.toString());
      }
      return this.http.get<any>(API_URL + 'inventory/batches', { params });
    } else {
      // Return empty array if productId is null
      return this.http.get<any>(API_URL + 'inventory/batches');
    }
  }

  // Notification endpoints
  getAllNotifications(filterType?: string, page?: number, size?: number): Observable<any> {
    let params = new HttpParams();
    if (filterType) params = params.set('filterType', filterType);
    if (page !== undefined && size !== undefined) {
      params = params.set('page', page.toString()).set('size', size.toString());
    }
    return this.http.get<any>(API_URL + 'notifications', { params });
  }

  getUnreadNotifications(): Observable<Notification[]> {
    return this.http.get<Notification[]>(API_URL + 'notifications/unread');
  }

  getNotificationsByType(type: string): Observable<Notification[]> {
    return this.http.get<Notification[]>(API_URL + 'notifications/type/' + type);
  }

  markNotificationAsRead(id: number): Observable<Notification> {
    return this.http.put<Notification>(API_URL + 'notifications/' + id + '/read', {}, httpOptions);
  }

  markAllNotificationsAsRead(): Observable<any> {
    return this.http.put(API_URL + 'notifications/read-all', {}, httpOptions);
  }

  getUnreadCount(): Observable<number> {
    return this.http.get<number>(API_URL + 'notifications/unread/count');
  }

  checkLowStock(): Observable<any> {
    return this.http.post(API_URL + 'notifications/check-low-stock', {}, httpOptions);
  }

  checkExpiry(daysBefore: number = 30): Observable<any> {
    return this.http.post(API_URL + 'notifications/check-expiry', null, {
      ...httpOptions,
      params: new HttpParams().set('daysBefore', daysBefore.toString())
    });
  }

  // Report endpoints
  getDashboardData(): Observable<Dashboard> {
    return this.http.get<Dashboard>(API_URL + 'reports/dashboard');
  }

  getStockMovements(filter: ReportFilter): Observable<any[]> {
    let params = new HttpParams();
    if (filter.startDate) params = params.set('startDate', filter.startDate);
    if (filter.endDate) params = params.set('endDate', filter.endDate);
    if (filter.productId) params = params.set('productId', filter.productId.toString());
    if (filter.category) params = params.set('category', filter.category);
    return this.http.get<any[]>(API_URL + 'reports/stock-movement', { params });
  }

  getInventoryValueOverTime(startDate?: string, endDate?: string): Observable<any[]> {
    let params = new HttpParams();
    if (startDate) params = params.set('startDate', startDate);
    if (endDate) params = params.set('endDate', endDate);
    return this.http.get<any[]>(API_URL + 'reports/inventory-value', { params });
  }

  getExpiryForecast(daysAhead: number = 30): Observable<any[]> {
    return this.http.get<any[]>(API_URL + 'reports/expiry-forecast', {
      params: new HttpParams().set('daysAhead', daysAhead.toString())
    });
  }

  getTopProducts(limit: number = 10, startDate?: string, endDate?: string): Observable<any[]> {
    let params = new HttpParams().set('limit', limit.toString());
    if (startDate) params = params.set('startDate', startDate);
    if (endDate) params = params.set('endDate', endDate);
    return this.http.get<any[]>(API_URL + 'reports/top-products', { params });
  }

  getMonthlySummary(year: number, month: number): Observable<any> {
    return this.http.get<any>(API_URL + 'reports/monthly-summary', {
      params: new HttpParams().set('year', year.toString()).set('month', month.toString())
    });
  }

  updateTransactionType(transactionId: number, transactionType: string): Observable<InventoryTransaction> {
    return this.http.put<InventoryTransaction>(API_URL + 'inventory/transactions/' + transactionId + '/transaction-type?transactionType=' + transactionType, {}, httpOptions);
  }
}

